// CRUMBLE CAKE (§6.4, §12.5): a hex-tile birthday cake over a milk sea. The 37 tiles are one
// InstancedMesh of frosted hex prisms (top 0.2 m pink frosting with a piped cream border, then
// sponge / cream / sponge layers; outer-ring tiles drip frosting over their outward faces). Tiles
// diff the replicated tile states: CRACKING wobbles and spreads cracks, FALLING drops with crumbs,
// GONE hides, REGROW rises with a sprinkle shower and a squash-and-stretch pop (§11.4). Each tile
// carries 12 instanced sprinkles and the six ring-2 corner tiles carry striped candles whose flame
// grows over the 1-bar telegraph (with a ground ring marking the 2 m reach) before a flame ring
// bursts out. The cake rests on a turned porcelain column (the stand) under the center tile that
// sinks with that tile; giant chrome forks, cherries and wafer sticks float in the fog band.
import {
  AdditiveBlending, CapsuleGeometry, Color, CylinderGeometry, DoubleSide, DynamicDrawUsage, ExtrudeGeometry, Group, InstancedBufferAttribute,
  InstancedMesh, Matrix4, PlaneGeometry, Quaternion, ShaderMaterial, SphereGeometry, TorusGeometry, Vector3,
} from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { RoundPhase, TileState } from '@shared/ids';
import { CAKE_GRID, CANDLE_TILES, TUNING } from '@shared/maps/cake';
import { hexIndex } from '@shared/maps/geometry';
import { finishBatch } from '../MapView';
import { Batch, hexShape, hullGeometry, prep, seeded, sweep, type ProfilePoint } from '../kit/geometry';
import { outlineMaterial, surfaceMaterial } from '../kit/materials';
import { B, type Arena, type ArenaFrame, type ArenaKit, type ArenaLayout } from './types';

const G = CAKE_GRID;
const R = G.R;
const APOTHEM = G.flat / 2;
const TILE_H = 1.0;
const FROST_H = 0.2;
const GAP = 0.035;
const FROSTING = '#FFB3C7';
const CREAM = '#FFF1E0';
const SPONGE = '#F2C28B';
const CHOCOLATE = '#6B3E2E';
const MILK = '#F7F7FF';
const CAUSTIC = '#DDE8FF';
/** §12.5 sprinkle colors. */
export const SPRINKLE_COLORS = ['#FF5A4E', '#FFC93C', '#3DDC97', '#4CB8FF', '#9B6BFF'] as const;
const SPRINKLES_PER_TILE: Readonly<Record<string, number>> = { high: 12, medium: 12, low: 8, potato: 6 };
const TICKS_PER_SEC = 60;
/** §6.4 durations (ticks). */
const CRACK_TICKS = 48;
const CRACK_TICKS_SD = 24;
const FALL_TICKS = 30;
const REGROW_TICKS = 30;
/** §6.4 flame reach: the dashed telegraph ring and the burst ring end where the sim pushes. */
const FLAME_REACH = TUNING.candle.flameRadius;

const glslColor = (c: string): string => `vec3(${new Color(c).toArray().map((v) => v.toFixed(4)).join(', ')})`;

const TILE_PARS = /* glsl */ `
varying float vWarn;
varying float vDrip;
varying float vCrack;
varying float vGlow;
varying float vSeed;
`;

const TILE_ALBEDO = /* glsl */ `
  vec3 frost = ${glslColor(FROSTING)};
  vec3 cream = ${glslColor(CREAM)};
  vec3 sponge = ${glslColor(SPONGE)};
  vec3 choc = ${glslColor(CHOCOLATE)};
  vec2 lp = envObj.xz;
  vec2 ap = abs(lp);
  float hexD = max(ap.x * 0.8660254 + ap.y * 0.5, ap.y) - ${(APOTHEM - GAP).toFixed(4)};
  float ang = atan(lp.y, lp.x);
  vec3 col;
  if (envNo.y > 0.55) {
    // Frosting top: soft swirl, a piped cream border of beads.
    float sw = envNoise(lp * 2.2 + vSeed * 13.0);
    col = frost * (0.95 + 0.08 * sw);
    // A soft dome: the frosting swells in the middle and dips toward the piped border.
    col *= 0.96 + 0.07 * smoothstep(-0.25, -0.95, hexD);
    float bead = 0.5 + 0.5 * sin(ang * 30.0);
    float border = smoothstep(-0.2, -0.13, hexD) * (0.55 + 0.45 * bead);
    col = mix(col, cream, border);
    // Sudden Death: the next tile to crumble flushes hot red.
    if (vWarn > 0.001) col = mix(col, vec3(1.0, 0.3, 0.26), vWarn * (0.45 + 0.25 * sin(uTime * 18.0)));
    // Cracks spread from the middle (CRACKING).
    if (vCrack > 0.001) {
      vec2 vor = envVoronoi(lp * 2.6 + vSeed * 31.0);
      float lines = 1.0 - smoothstep(0.02, 0.07, vor.y);
      float reach = vCrack * 1.4;
      col = mix(col, choc, lines * (1.0 - smoothstep(reach - 0.25, reach, length(lp))));
    }
  } else if (envNo.y < -0.55) {
    col = choc;
  } else {
    // Sides: frosting band (dripping on outward faces), then sponge / cream / sponge.
    int side = int(floor(mod(ang, 6.2831853) / 1.0471976));
    float bits = vDrip;
    float on = 0.0;
    for (int k = 0; k < 6; k++) {
      float b = mod(bits, 2.0);
      bits = floor(bits / 2.0);
      if (k == side) on = b;
    }
    float s = ang * 3.0;
    float drip = 0.04 + on * (0.08 + 0.36 * pow(envNoise(vec2(s * 2.3 + vSeed * 17.0, 1.7)), 2.0));
    float wave = 0.025 * sin(s * 9.0 + vSeed * 5.0);
    float frostLine = -${FROST_H.toFixed(2)} - drip + wave;
    float y = envObj.y;
    float pores = step(0.82, envNoise(envObj.xz * 21.0 + vec2(y * 23.0, 0.0)));
    vec3 sp = sponge * (0.92 + 0.08 * envNoise(vec2(ang * 10.0, y * 14.0))) * (1.0 - 0.18 * pores);
    col = sp;
    col = mix(col, cream, step(-0.62, y) * step(y, -0.5));
    col = mix(col, choc * 1.2, step(y, -0.9));
    col = mix(col, frost, step(frostLine, y));
  }
  albedo = col;
`;

const TILE_EMISSIVE = /* glsl */ `
  if (vWarn > 0.001) {
    // Sudden Death: the next tile to crumble pulses red, its rim brightest.
    vec2 wp = abs(envObj.xz);
    float wd = max(wp.x * 0.8660254 + wp.y * 0.5, wp.y) - ${(APOTHEM - GAP).toFixed(4)};
    float rimW = smoothstep(-0.35, -0.02, wd);
    float pulse = 0.55 + 0.45 * sin(uTime * 18.0);
    glow += vec3(1.0, 0.18, 0.12) * vWarn * pulse * (0.25 + 0.9 * rimW) * step(0.55, envNo.y);
  }
  if (vGlow > 0.001) {
    float sparkle = step(0.9, envNoise(envObj.xz * 9.0 + vec2(uTime * 3.0, envObj.y * 9.0)));
    glow += vec3(1.0, 0.9, 0.7) * vGlow * (0.25 + sparkle * 1.4);
  }
  if (vCrack > 0.001) glow += vec3(0.6, 0.15, 0.1) * vCrack * 0.25 * (0.5 + 0.5 * sin(uTime * 20.0));
`;

const CANDLE_ALBEDO = /* glsl */ `
  float ang = atan(envObj.z, envObj.x);
  float stripe = step(0.5, fract(ang / 6.2831853 * 3.0 + envObj.y * 0.9));
  albedo = mix(${glslColor('#FFFFFF')}, ${glslColor('#FF8FB1')}, stripe);
  if (envObj.y > ${(TUNING.candle.top - 0.12).toFixed(2)}) albedo = ${glslColor(CREAM)};
`;

const FLAME_VERTEX = /* glsl */ `
attribute vec2 aFlame;   // x = size, y = intensity
varying vec2 vUv;
varying float vI;
uniform float uTime;
void main() {
  vUv = uv;
  vI = aFlame.y;
  vec4 center = modelViewMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
  float flick = 1.0 + 0.12 * sin(uTime * 23.0 + instanceMatrix[3].x * 3.0) + 0.08 * sin(uTime * 37.0);
  vec2 off = position.xy * vec2(aFlame.x * 0.6, aFlame.x * flick);
  center.xy += off;
  gl_Position = projectionMatrix * center;
}`;

const FLAME_FRAGMENT = /* glsl */ `
varying vec2 vUv;
varying float vI;
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  p.y += 0.35;
  // A teardrop: round bottom, pointed top.
  float w = 0.62 * (1.0 - smoothstep(-0.6, 1.2, p.y)) + 0.05;
  float d = length(vec2(p.x / max(w, 0.05), min(p.y, 0.0) * 1.6));
  d = max(d, abs(p.x) / max(w, 0.05));
  float a = (1.0 - smoothstep(0.55, 1.0, d)) * step(p.y, 1.0);
  float core = 1.0 - smoothstep(0.0, 0.6, d);
  vec3 col = mix(vec3(1.0, 0.45, 0.1), vec3(1.0, 0.95, 0.6), core);
  float alpha = clamp(a * vI, 0.0, 1.0);
  gl_FragColor = vec4(col * alpha * (1.0 + 0.6 * vI), alpha);
}`;

const RING_VERTEX = /* glsl */ `
attribute vec2 aRing;     // x = radius, y = alpha
varying vec2 vUv;
varying float vA;
varying float vR;
void main() {
  vUv = uv;
  vA = aRing.y;
  vR = aRing.x;
  vec3 p = position * aRing.x;
  gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(p, 1.0);
}`;

const RING_FRAGMENT = /* glsl */ `
uniform vec3 uColor;
uniform vec3 uColor2;
uniform float uWidth;
uniform float uTime;
varying vec2 vUv;
varying float vA;
varying float vR;
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  float w = uWidth / max(vR, 0.05);
  float ring = 1.0 - smoothstep(0.0, w, abs(r - (1.0 - w)));
  float fill = (1.0 - smoothstep(0.85, 1.0, r)) * 0.18;
  float dash = 0.75 + 0.25 * step(0.5, fract(atan(p.y, p.x) / 6.2831853 * 18.0 - uTime * 0.6));
  float a = (ring * dash + fill) * vA;
  if (a < 0.003) discard;
  gl_FragColor = vec4(mix(uColor2, uColor, ring) * a, a);
}`;

/** One tile's presentation state. */
/** `timer0`: the largest `tileTimer` seen in the current state (its full length when seen from the start). */
interface TileAnim { state: number; at: number; seed: number; prevTick: number; settle: number; timer0: number }

export interface CakeDebug {
  tiles: InstancedMesh;
  /** 'hidden' | 'wobble' | 'falling' | 'rising' | 'solid' per tile, from the presented transform. */
  tileLook(i: number): string;
  tileY(i: number): number;
  tileScale(i: number): number;
}

export function buildCake(kit: ArenaKit): Arena & { cake: CakeDebug } {
  const root = new Group();
  root.name = 'arena:cake';
  const owned: { dispose(): void }[] = [];
  const p = kit.palette;
  const env = kit.env;
  const n = G.count;
  const rnd = seeded(kit.seed * 5 + 11);

  // ---- Tiles ---------------------------------------------------------------------------------
  const tileGeo = new ExtrudeGeometry(hexShape(R - GAP - 0.05), {
    depth: TILE_H - 0.12, bevelEnabled: true, bevelThickness: 0.06, bevelSize: 0.05, bevelSegments: kit.rich ? 3 : 2, curveSegments: 1,
  });
  // Shape XY → sim XY; extrude +Z → down (proper rotation); top at y = 0.
  tileGeo.applyMatrix4(new Matrix4().set(1, 0, 0, 0, 0, 0, -1, -0.06, 0, 1, 0, 0, 0, 0, 0, 1));
  tileGeo.computeVertexNormals();
  const drip = new Float32Array(n);
  const crack = new Float32Array(n);
  const glowA = new Float32Array(n);
  const seedA = new Float32Array(n);
  const crackAttr = new InstancedBufferAttribute(crack, 1).setUsage(DynamicDrawUsage);
  const glowAttr = new InstancedBufferAttribute(glowA, 1).setUsage(DynamicDrawUsage);
  const warnA = new Float32Array(n);
  const warnAttr = new InstancedBufferAttribute(warnA, 1).setUsage(DynamicDrawUsage);
  for (let i = 0; i < n; i++) {
    seedA[i] = rnd();
    // Outward faces (no neighbor across the face) get frosting drips.
    let mask = 0;
    for (let k = 0; k < 6; k++) {
      const a = Math.PI / 6 + (k * Math.PI) / 3;
      const nx = G.cx[i] + Math.cos(a) * G.flat;
      const ny = G.cy[i] + Math.sin(a) * G.flat;
      let has = false;
      for (let j = 0; j < n; j++) if (Math.abs(G.cx[j] - nx) < 0.1 && Math.abs(G.cy[j] - ny) < 0.1) has = true;
      if (!has) mask |= 1 << k;
    }
    drip[i] = mask;
  }
  tileGeo.setAttribute('aDrip', new InstancedBufferAttribute(drip, 1));
  tileGeo.setAttribute('aCrack', crackAttr);
  tileGeo.setAttribute('aGlow', glowAttr);
  tileGeo.setAttribute('aWarn', warnAttr);
  tileGeo.setAttribute('aSeed', new InstancedBufferAttribute(seedA, 1));
  const tileMat = surfaceMaterial(env, kit.ramp, {
    key: 'cake-tile', color: '#FFFFFF', rim: 0.3, gloss: 0.45, glossEdge: 0.95, pars: TILE_PARS,
    vertexPars: 'attribute float aDrip;\nattribute float aCrack;\nattribute float aGlow;\nattribute float aSeed;\nattribute float aWarn;\n',
    vertexEnd: 'vDrip = aDrip; vCrack = aCrack; vGlow = aGlow; vSeed = aSeed; vWarn = aWarn;',
    albedo: TILE_ALBEDO, emissive: TILE_EMISSIVE,
  });
  const tiles = new InstancedMesh(tileGeo, tileMat, n);
  tiles.name = 'cake-tiles';
  tiles.castShadow = true;
  tiles.receiveShadow = true;
  tiles.instanceMatrix.setUsage(DynamicDrawUsage);
  tiles.frustumCulled = false;
  root.add(tiles);
  owned.push(tileGeo, tileMat);
  const tileHullSrc = new ExtrudeGeometry(hexShape(R - GAP - 0.05), { depth: TILE_H - 0.12, bevelEnabled: true, bevelThickness: 0.06, bevelSize: 0.05, bevelSegments: 1, curveSegments: 1 });
  tileHullSrc.applyMatrix4(new Matrix4().set(1, 0, 0, 0, 0, 0, -1, -0.06, 0, 1, 0, 0, 0, 0, 0, 1));
  const tileHullGeo = hullGeometry(tileHullSrc);
  tileHullSrc.dispose();
  const tileHullMat = outlineMaterial(0.04, kit.outlineScale);
  const tileHull = new InstancedMesh(tileHullGeo, tileHullMat, n);
  tileHull.instanceMatrix = tiles.instanceMatrix;
  tileHull.frustumCulled = false;
  tileHull.name = 'cake-tiles-outline';
  root.add(tileHull);
  owned.push(tileHullGeo, tileHullMat);

  // ---- Sprinkles (instanced, following their tiles) --------------------------------------------
  const per = SPRINKLES_PER_TILE[kit.tier];
  const sprGeo = new CapsuleGeometry(0.035, 0.13, 1, kit.rich ? 5 : 4);
  sprGeo.rotateZ(Math.PI / 2);
  const sprMat = surfaceMaterial(env, kit.ramp, { key: 'cake-sprinkle', color: '#FFFFFF', rim: 0.2, gloss: 0.6 });
  const sprinkles = new InstancedMesh(sprGeo, sprMat, n * per);
  sprinkles.name = 'cake-sprinkles';
  sprinkles.instanceMatrix.setUsage(DynamicDrawUsage);
  sprinkles.frustumCulled = false;
  sprinkles.receiveShadow = true;
  root.add(sprinkles);
  owned.push(sprGeo, sprMat);
  const sprLocal: Matrix4[] = [];
  const col = new Color();
  for (let i = 0; i < n; i++) {
    for (let k = 0; k < per; k++) {
      // Rejection-sample inside the hex top, clear of the candle.
      let x = 0;
      let y = 0;
      for (let tries = 0; tries < 20; tries++) {
        x = (rnd() * 2 - 1) * (R - 0.35);
        y = (rnd() * 2 - 1) * (APOTHEM - 0.3);
        const inHex = Math.max(Math.abs(x) * 0.866 + Math.abs(y) * 0.5, Math.abs(y)) < APOTHEM - 0.3;
        if (inHex && (!CANDLE_TILES.includes(i) || Math.hypot(x, y) > TUNING.candle.radius + 0.2)) break;
      }
      sprLocal.push(new Matrix4().makeRotationY(rnd() * Math.PI).setPosition(x, 0.035, y));
      col.set(SPRINKLE_COLORS[Math.floor(rnd() * SPRINKLE_COLORS.length)]);
      sprinkles.setColorAt(i * per + k, col);
    }
  }

  // ---- Candles, flames, danger rings, flame rings ----------------------------------------------
  const C = TUNING.candle;
  const candleGeo = sweep([[[0, 0], [C.radius, 0], [C.radius, C.top - 0.08], [C.radius - 0.04, C.top], [0.12, C.top - 0.03], [0, C.top - 0.02]]], kit.seg(20, 10));
  const wick = prep(new CylinderGeometry(0.025, 0.03, 0.18, 5).translate(0, C.top + 0.05, 0), null, { color: CHOCOLATE });
  const candleMat = surfaceMaterial(env, kit.ramp, { key: 'cake-candle', color: '#FFFFFF', rim: 0.35, gloss: 0.5, albedo: CANDLE_ALBEDO });
  const candles = new InstancedMesh(candleGeo, candleMat, CANDLE_TILES.length);
  candles.name = 'candles';
  candles.castShadow = true;
  candles.instanceMatrix.setUsage(DynamicDrawUsage);
  candles.frustumCulled = false;
  root.add(candles);
  const wicks = new InstancedMesh(wick, kit.mats.toon, CANDLE_TILES.length);
  wicks.instanceMatrix = candles.instanceMatrix;
  wicks.frustumCulled = false;
  root.add(wicks);
  const candleHullSrc = new CylinderGeometry(C.radius, C.radius, C.top, 14).translate(0, C.top / 2, 0);
  const candleHullGeo = hullGeometry(candleHullSrc);
  candleHullSrc.dispose();
  const candleHull = new InstancedMesh(candleHullGeo, kit.mats.hull, CANDLE_TILES.length);
  candleHull.instanceMatrix = candles.instanceMatrix;
  candleHull.frustumCulled = false;
  root.add(candleHull);
  owned.push(candleGeo, wick, candleMat, candleHullGeo);

  const flameGeo = new PlaneGeometry(1, 1.6).translate(0, 0.55, 0);
  const flameData = new Float32Array(CANDLE_TILES.length * 2);
  const flameAttr = new InstancedBufferAttribute(flameData, 2).setUsage(DynamicDrawUsage);
  flameGeo.setAttribute('aFlame', flameAttr);
  const flameMat = new ShaderMaterial({
    uniforms: { uTime: env.uTime }, vertexShader: FLAME_VERTEX, fragmentShader: FLAME_FRAGMENT,
    transparent: true, depthWrite: false, premultipliedAlpha: true, toneMapped: false,
  });
  const flames = new InstancedMesh(flameGeo, flameMat, CANDLE_TILES.length);
  flames.name = 'candle-flames';
  flames.instanceMatrix.setUsage(DynamicDrawUsage);
  flames.frustumCulled = false;
  flames.renderOrder = 5;
  root.add(flames);
  owned.push(flameGeo, flameMat);

  const ringGeo = new PlaneGeometry(2, 2).rotateX(-Math.PI / 2);
  const makeRings = (name: string, color: string, color2: string, width: number, additive: boolean): { mesh: InstancedMesh; data: Float32Array; attr: InstancedBufferAttribute } => {
    const g = ringGeo.clone();
    const data = new Float32Array(CANDLE_TILES.length * 2);
    const attr = new InstancedBufferAttribute(data, 2).setUsage(DynamicDrawUsage);
    g.setAttribute('aRing', attr);
    const mat = new ShaderMaterial({
      uniforms: { uColor: { value: new Color(color) }, uColor2: { value: new Color(color2) }, uWidth: { value: width }, uTime: env.uTime },
      vertexShader: RING_VERTEX, fragmentShader: RING_FRAGMENT, transparent: true, depthWrite: false, side: DoubleSide, toneMapped: false,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
    });
    if (additive) mat.blending = AdditiveBlending;
    else mat.premultipliedAlpha = true;
    const mesh = new InstancedMesh(g, mat, CANDLE_TILES.length);
    mesh.name = name;
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.renderOrder = 4;
    root.add(mesh);
    owned.push(g, mat);
    return { mesh, data, attr };
  };
  const warnRings = makeRings('candle-warn', '#FF7A3C', '#FFC93C', 0.09, false);
  const burstRings = makeRings('flame-rings', '#FFB347', '#FF4D1A', 0.32, true);
  owned.push(ringGeo);

  // ---- Underside and background ----------------------------------------------------------------
  const standGroup = standAndProps(kit, root);
  const centerTile = hexIndex(G, 0, 0);

  // ---- Animation -------------------------------------------------------------------------------
  const anim: TileAnim[] = Array.from({ length: n }, () => ({ state: TileState.SOLID, at: 0, seed: 0, prevTick: 0, settle: 0, timer0: 0 }));
  for (let i = 0; i < n; i++) anim[i].seed = seedA[i] * 100;
  const looks: string[] = new Array<string>(n).fill('solid');
  const tileY = new Float32Array(n);
  const tileS = new Float32Array(n).fill(1);
  let first = true;
  const m = new Matrix4();
  const m2 = new Matrix4();
  const q = new Quaternion();
  const qa = new Quaternion();
  const ax = new Vector3();
  const pos = new Vector3();
  const scl = new Vector3();
  const tileMats: Matrix4[] = Array.from({ length: n }, () => new Matrix4());
  const candleIndex = new Map<number, number>(CANDLE_TILES.map((t, i) => [t, i]));
  const tmpV = new Vector3();

  function crumbs(i: number): void {
    kit.particles.burst({ x: G.cx[i], y: -0.1, z: G.cy[i], count: 16, color: [SPONGE, SPONGE, FROSTING, CREAM, CHOCOLATE], speed: 2.2, up: 3.2, size: 0.1, life: 1.3, spread: R * 0.7, gravity: 14 });
  }
  function shower(i: number): void {
    kit.particles.burst({ x: G.cx[i], y: 2.6, z: G.cy[i], count: 18, color: SPRINKLE_COLORS, speed: 0.6, up: -1, size: 0.07, life: 1.1, spread: R * 0.8, gravity: 9, stretch: 2.6 });
  }

  function poseTile(i: number, f: ArenaFrame, tick: number, timer: number): void {
    const a = anim[i];
    // Elapsed ticks in the state: from the replicated countdown (exact after joins and in replays)
    // when it runs, else since the view saw the state change.
    const nominal = a.state === TileState.FALLING ? FALL_TICKS : a.state === TileState.REGROW ? REGROW_TICKS : a.state === TileState.CRACKING ? a.timer0 : 0;
    const el = timer > 0 && nominal > 0 ? Math.max(0, nominal - timer + (tick - Math.floor(tick))) : Math.max(0, tick - a.at);
    const t = el / TICKS_PER_SEC;
    let y = 0;
    let sy = 1;
    let sxz = 1;
    let rot = 0;
    let look = 'solid';
    crack[i] = 0;
    glowA[i] = 0;
    q.identity();
    switch (a.state) {
      case TileState.CRACKING: {
        const dur = a.timer0 > 0 ? a.timer0 : f.view.clock.phase === RoundPhase.SUDDEN_DEATH ? CRACK_TICKS_SD : CRACK_TICKS;
        const k = Math.min(1, el / dur);
        crack[i] = k;
        const amp = 0.015 + 0.05 * k;
        ax.set(Math.cos(a.seed), 0, Math.sin(a.seed));
        rot = amp * Math.sin(t * 2 * Math.PI * 7 + a.seed);
        q.setFromAxisAngle(ax, rot);
        y = -0.02 * k + 0.012 * Math.sin(t * 50 + a.seed);
        look = 'wobble';
        break;
      }
      case TileState.FALLING: {
        const tt = Math.min(t, FALL_TICKS / TICKS_PER_SEC + 0.6);
        y = -0.5 * 18 * tt * tt - 0.4 * tt;
        ax.set(Math.cos(a.seed), 0, Math.sin(a.seed));
        q.setFromAxisAngle(ax, 0.9 * tt * tt + 0.3 * tt);
        crack[i] = 1;
        look = y < -6 ? 'hidden' : 'falling';
        break;
      }
      case TileState.GONE:
        look = 'hidden';
        break;
      case TileState.REGROW: {
        const k = Math.min(1, el / REGROW_TICKS);
        const e = 1 - Math.pow(1 - k, 3);
        y = -1.3 * (1 - e);
        sy = 0.25 + 0.75 * e + 0.18 * Math.sin(k * Math.PI);
        sxz = 0.7 + 0.3 * e;
        glowA[i] = 1 - k * 0.6;
        look = 'rising';
        break;
      }
      default: {
        // The Sudden Death next-to-crumble tile hops nervously; after regrowing, a squash bounce settles.
        if (warnA[i] > 0) y = 0.05 * Math.abs(Math.sin(t * 22 + a.seed));
        // Settle after regrowing: a squash bounce.
        const s = a.settle > 0 ? Math.max(0, 1 - (tick - a.settle) / 18) : 0;
        sy = 1 - 0.08 * s * Math.sin((1 - s) * Math.PI * 3);
        glowA[i] = 0.4 * s;
      }
    }
    looks[i] = look;
    tileY[i] = y;
    tileS[i] = look === 'hidden' ? 0 : sy;
    if (look === 'hidden') scl.set(0, 0, 0);
    else scl.set(sxz, sy, sxz);
    pos.set(G.cx[i], y, G.cy[i]);
    tileMats[i].compose(pos, q, scl);
    tiles.setMatrixAt(i, tileMats[i]);
    for (let k = 0; k < per; k++) {
      m.multiplyMatrices(tileMats[i], sprLocal[i * per + k]);
      sprinkles.setMatrixAt(i * per + k, m);
    }
    const ci = candleIndex.get(i);
    if (ci !== undefined) {
      // Candles ride their tile; in Sudden Death they topple as it falls.
      m.copy(tileMats[i]);
      if (a.state === TileState.FALLING) {
        ax.set(-Math.sin(a.seed), 0, Math.cos(a.seed));
        qa.setFromAxisAngle(ax, Math.min(1.4, 3 * t * t + 0.8 * t));
        m.multiply(m2.makeRotationFromQuaternion(qa));
      }
      if (look === 'hidden') m.makeScale(0, 0, 0);
      candles.setMatrixAt(ci, m);
    }
  }

  const layout: ArenaLayout = {
    sky: { top: p.skyTop, bottom: p.skyBottom, cloud: '#FFFFFF', clouds: 0.75, stars: 0, sunGlow: 0.95 },
    voidColors: { a: '#EEF0FA', b: CAUSTIC, c: '#FFE1EC' },
    voidEdge: 8.2,
    voidShadow: { x: 0, y: 0, hx: 0, hy: 0, r: 7.6, strength: 0.75 },
    voidPour: { x: 0, y: 0, r: 2.1 },
    splash: { ring: '#FFFFFF', edge: CAUSTIC },
    audience: { cx: 0, cy: 0, rx: 14, ry: 13.2, y: -0.9, plank: '#F2C28B', trim: '#FF8FB1', fur: ['#FFB3C7', '#FFF1E0', '#9DE0C8', '#B9E6FF', '#FFC93C', '#C8B6FF', '#FF8FB1'] },
    confetti: { colors: SPRINKLE_COLORS, r0: 18, r1: 40, y0: -2, y1: 18 },
    shadowY: [-1.2, 3.4],
  };

  return {
    root, layout,
    cake: {
      tiles,
      tileLook: (i) => looks[i],
      tileY: (i) => tileY[i],
      tileScale: (i) => tileS[i],
    },
    update(f) {
      const v = f.view;
      const tick = v.roundTick;
      const states = v.map.tileState;
      const timers = v.map.tileTimer;
      const nextTile = v.hazard.sdNextTile ?? -1;
      const nextWarn = v.hazard.sdNextWarn ?? 0;
      for (let i = 0; i < n; i++) {
        const a = anim[i];
        const st = states[i] ?? TileState.SOLID;
        const timer = timers[i] ?? 0;
        warnA[i] = i === nextTile ? 0.4 + 0.6 * nextWarn : 0;
        if (st !== a.state) {
          const prev = a.state;
          a.state = st;
          a.at = tick;
          a.timer0 = timer;
          if (first) a.at = tick - 600;
          else if (st === TileState.FALLING) crumbs(i);
          else if (st === TileState.REGROW) shower(i);
          else if (st === TileState.SOLID && prev === TileState.REGROW) a.settle = tick;
        }
        a.timer0 = Math.max(a.timer0, timer);
        // A jump back in time (replay, seek) restarts the state's clock.
        if (tick < a.prevTick - 1) a.at = Math.min(a.at, tick);
        a.prevTick = tick;
        poseTile(i, f, tick, timer);
      }
      first = false;
      // The stand sinks into the milk with the center tile and rises back with its regrowth, so a
      // hole in the middle of the cake never shows a plate that reads as floor.
      standGroup.visible = looks[centerTile] !== 'hidden';
      standGroup.position.y = Math.min(0, tileY[centerTile]);
      tiles.instanceMatrix.needsUpdate = true;
      sprinkles.instanceMatrix.needsUpdate = true;
      candles.instanceMatrix.needsUpdate = true;
      crackAttr.needsUpdate = true;
      glowAttr.needsUpdate = true;
      warnAttr.needsUpdate = true;
      // Candle telegraphs (§6.4, §11.4): flicker grows over 1 bar, then the flame ring bursts.
      const hz = v.hazard;
      for (let ci = 0; ci < CANDLE_TILES.length; ci++) {
        const ti = CANDLE_TILES[ci];
        const hidden = looks[ti] === 'hidden' || anim[ti].state === TileState.FALLING;
        const flick = hz.candleFlicker[ci] ?? 0;
        const flame = hz.candleFlame[ci] ?? 0;
        tmpV.set(0, C.top + 0.12, 0).applyMatrix4(tileMats[ti]);
        m.makeTranslation(tmpV.x, tmpV.y, tmpV.z);
        flames.setMatrixAt(ci, m);
        flameData[ci * 2] = hidden ? 0 : 0.78 + 0.8 * flick + 0.8 * flame;
        flameData[ci * 2 + 1] = hidden ? 0 : 0.85 + 0.5 * flick + flame;
        m.makeTranslation(G.cx[ti], 0.025, G.cy[ti]);
        warnRings.mesh.setMatrixAt(ci, m);
        warnRings.data[ci * 2] = FLAME_REACH;
        warnRings.data[ci * 2 + 1] = hidden ? 0 : Math.min(1, flick * 1.3) * (0.55 + 0.45 * Math.sin(f.time * 12)) + flame * 0.4;
        m.makeTranslation(G.cx[ti], 0.6, G.cy[ti]);
        burstRings.mesh.setMatrixAt(ci, m);
        burstRings.data[ci * 2] = 0.5 + (FLAME_REACH - 0.3) * Math.sqrt(Math.max(0, 1 - flame));
        burstRings.data[ci * 2 + 1] = hidden ? 0 : flame;
      }
      flames.instanceMatrix.needsUpdate = true;
      flameAttr.needsUpdate = true;
      for (const r of [warnRings, burstRings]) {
        r.mesh.instanceMatrix.needsUpdate = true;
        r.attr.needsUpdate = true;
      }
    },
    onEvent() {
      // Tiles animate from the replicated states (§6.4); TILE events exist for stats and tests.
    },
    dispose() {
      for (const o of owned) o.dispose();
    },
  };
}

/** The cake stand under the cake and the dessert props floating in the fog band. */
function standAndProps(kit: ArenaKit, root: Group): Group {
  const bg = kit.batch;
  const seg = (k: number): number => kit.seg(k, 8);
  // The stand is its own group: it belongs to the center tile and sinks with it (see update).
  const standBatch = new Batch();
  const standGroup = new Group();
  standGroup.name = 'cake-stand';
  // Cake stand: a turned porcelain column with gold bands under the center tile, its flared foot
  // standing in the milk. No plate: anything wider than the column would show through a hole next
  // to the center tile and read as floor (the column sits inside the center tile's footprint).
  const standRuns: ProfilePoint[] = [
    [0, -1.0], [0.6, -1.0], [0.68, -1.06], [0.6, -1.14], [0.46, -1.24], [0.42, -1.5], [0.5, -1.75], [0.62, -1.92], [0.58, -2.06],
    [0.42, -2.22], [0.36, -2.42], [0.4, -2.6], [0.62, -2.76], [1.15, -2.9], [1.95, -2.99], [2.08, -3.06], [2.02, -3.2],
  ];
  const stand = sweep([standRuns], seg(36));
  standBatch.add(B.gloss, stand, null, { color: '#FFF8F0', ao: [-3.15, -1.5, 0.72] });
  for (const [r, y, t] of [[0.66, -1.06, 0.04], [0.61, -1.92, 0.05], [0.62, -2.76, 0.05]] as const) {
    standBatch.add(B.gloss, new TorusGeometry(r, t, 6, seg(32)).rotateX(Math.PI / 2), new Matrix4().makeTranslation(0, y, 0), { color: '#E8B64C' });
  }
  if (kit.rich) standBatch.add(B.hull, sweep([standRuns.slice(4)], 24), null);
  // Background props: forks standing in the milk, floating cherries and wafer sticks.
  const steel = '#E4EAF1';
  for (const [x, z, yaw, lean] of [[-14, -13, 0.5, 0.35], [15.5, -11, -0.4, -0.3]] as const) {
    const fm = new Matrix4().makeRotationY(yaw).multiply(new Matrix4().makeRotationZ(lean)).setPosition(x, -3.2, z);
    const handle = new RoundedBoxGeometry(0.9, 9, 0.35, 2, 0.15);
    bg.add(B.chrome, handle, fm.clone().multiply(new Matrix4().makeTranslation(0, 4.5, 0)), { color: steel });
    const neck = new RoundedBoxGeometry(1.8, 1.4, 0.3, 2, 0.12);
    bg.add(B.chrome, neck, fm.clone().multiply(new Matrix4().makeTranslation(0, 9.4, 0)), { color: steel });
    for (let k = 0; k < 4; k++) {
      const tine = new CapsuleGeometry(0.13, 2.4, 2, 6);
      bg.add(B.chrome, tine, fm.clone().multiply(new Matrix4().makeTranslation(-0.66 + k * 0.44, 11.2, 0)), { color: steel });
    }
    if (kit.rich) bg.add(B.bgHull, new RoundedBoxGeometry(0.9, 9, 0.35, 1, 0.15), fm.clone().multiply(new Matrix4().makeTranslation(0, 4.5, 0)));
  }
  const cherries: [number, number][] = [[-11.5, -3], [11, 4], [-9, 8], [6.5, -12], [-5, -13.5], [12.5, -4.5]];
  for (const [x, z] of cherries) {
    bg.add(B.bg, new SphereGeometry(0.85, seg(16), seg(12)), new Matrix4().makeTranslation(x, -2.65, z), { color: '#E8213A', ao: [-3.4, -2.2, 0.6] });
    bg.add(B.bg, new SphereGeometry(0.2, 8, 6), new Matrix4().makeTranslation(x - 0.3, -2.1, z + 0.35), { color: '#FF8A9A' });
    const stem = new CylinderGeometry(0.05, 0.06, 1.5, 5);
    bg.add(B.bg, stem, new Matrix4().makeRotationZ(0.45).setPosition(x + 0.3, -1.25, z), { color: '#4E8A2E' });
    if (kit.rich) bg.add(B.bgHull, new SphereGeometry(0.85, 12, 8), new Matrix4().makeTranslation(x, -2.65, z));
  }
  for (const [x, z, yaw] of [[-12.5, 5.5, 0.7], [13, -1, -0.5], [2, -14.5, 1.3]] as const) {
    const wafer = new RoundedBoxGeometry(4.2, 0.55, 0.55, 2, 0.12);
    bg.add(B.bg, wafer, new Matrix4().makeRotationY(yaw).multiply(new Matrix4().makeRotationZ(0.1)).setPosition(x, -2.8, z), { color: '#E9B06A' });
    for (let k = 0; k < 5; k++) {
      const band = new TorusGeometry(0.3, 0.035, 4, 10);
      band.rotateY(Math.PI / 2);
      const d = -1.6 + k * 0.8;
      bg.add(B.bg, band, new Matrix4().makeRotationY(yaw).setPosition(x + Math.cos(yaw) * d, -2.8, z - Math.sin(yaw) * d), { color: '#C98A44' });
    }
  }
  finishBatch(kit, standBatch, standGroup, { receiveShadow: true });
  root.add(standGroup);
  return standGroup;
}
